import { createContext, Fragment, useContext, useEffect, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import { CompanyInfo } from "@/components/company-info.jsx";
import { EmailExchanges } from "@/components/email-exchanges.jsx";
import { CollectiveProcedures } from "@/components/collective-procedures.jsx";
import { BankTransactions } from "@/components/bank-transactions.jsx";
import { FiscalDataManager } from "@/components/fiscal-data-manager.jsx";
import RiskInfo from "./RiskInfo.jsx";
import FinancingSolutions from "./FinancingSolutions.jsx";
import { useCompany } from "@/lib/hooks/useCompanies.js";
import { getCurrentCompanyId } from "@/lib/companies.js";
import {
  ArrowLeft,
  AlertTriangle,
  Loader2,
  Users,
  Calendar,
  MapPin,
  Building,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { formatDate } from "date-fns";

const CompanyAnalysisContext = createContext(null);

function CompanyAnalysisProvider({ children }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const currentId = searchParams.get("companyId") || location.state?.companyId;

  const { data: company, isLoading, error } = useCompany(currentId || "");

  // Mock data for fields not available in API
  const mockCompanyData = useMemo(
    () => ({
      // Use API data if available, otherwise use mock
      score: company?.score ?? company?.score ?? null,
      scoreCalculatedAt:
        company?.scoreCalculatedAt ?? company?.score_calculated_at ?? null,
      lastLiasseAt:
        company?.lastLiasseAt ??
        company?.last_liasse_at ??
        company?.last_tax_declaration_date ??
        null,
      lastTransactionAt:
        company?.lastTransactionAt ??
        company?.last_transaction_at ??
        company?.last_transaction_date ??
        null,
      hasCollectiveProcedure:
        company?.hasCollectiveProcedure ??
        company?.has_collective_procedure ??
        false,
      sector: company?.sector ?? company?.activities_sector ?? null,
      avatarDataUrl: null,
    }),
    [company],
  );

  // Combine API data with mock data
  const enrichedCompany = useMemo(() => {
    if (!company) return null;
    return {
      ...company,
      ...mockCompanyData,
      // Ensure ID is string for consistency
      id: String(company.id),
    };
  }, [company, mockCompanyData]);

  // Format registration date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Vérifier si le dossier est complet
  const getMissingStatementMonths = (c) => {
    const months = [];
    const now = new Date();
    const windowMonths = [];
    for (let i = 5; i >= 0; i--) {
      windowMonths.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    }
    const lastTx = c?.lastTransactionAt ? new Date(c.lastTransactionAt) : null;
    const lastTxMonth = lastTx
      ? new Date(lastTx.getFullYear(), lastTx.getMonth(), 1)
      : null;

    const monthLabel = (d) => {
      const m = (d.getMonth() + 1).toString().padStart(2, "0");
      return `${m}/${d.getFullYear()}`;
    };

    for (const m of windowMonths) {
      if (!lastTxMonth) {
        months.push(monthLabel(m));
        continue;
      }
      if (m > lastTxMonth) {
        months.push(monthLabel(m));
      }
    }
    return months;
  };

  const hasCompleteData = (c) => {
    // Si la société a déjà un score de risque, on la considère comme complète (sociétés exemples)
    if (c?.score != null) {
      return true;
    }

    const currentYear = new Date().getFullYear();
    const hasCurrentYearLiasse = Boolean(
      c?.lastLiasseAt && new Date(c.lastLiasseAt).getFullYear() === currentYear,
    );
    const hasPreviousYearLiasse = Boolean(
      c?.lastLiasseAt &&
      new Date(c.lastLiasseAt).getFullYear() === currentYear - 1,
    );
    const hasAllStatements = getMissingStatementMonths(c).length === 0;

    return hasCurrentYearLiasse && hasPreviousYearLiasse && hasAllStatements;
  };

  const isDataIncomplete = enrichedCompany
    ? !hasCompleteData(enrichedCompany)
    : false;

  useEffect(() => {
    document.title = enrichedCompany
      ? `Étude de risque - ${enrichedCompany.name}`
      : "Étude d'entreprise";
  }, [enrichedCompany]);

  const value = {
    isLoading,
    error,
    formatDate,
    isDataIncomplete,
    enrichedCompany,
  };

  return (
    <CompanyAnalysisContext.Provider value={value}>
      {children}
    </CompanyAnalysisContext.Provider>
  );
}

function useCompanyAnalysis() {
  return useContext(CompanyAnalysisContext);
}

export default function CompanyAnalysis() {
  return (
    <CompanyAnalysisProvider>
      <CompanyAnalysisComponent />
    </CompanyAnalysisProvider>
  );
}

function CompanyAnalysisComponent() {
  const { isLoading, error, isDataIncomplete, enrichedCompany } =
    useCompanyAnalysis();

  if (isLoading) return <CompanyAnalysisLoading />;
  if (error) return <CompanyAnalysisError />;
  if (!enrichedCompany) return <CompanyAnalysisNone />;

  return (
    <div className="p-6 space-y-6">
      <CompanyAnalysisHeader />

      {isDataIncomplete && <CompanyAnalysisIncompleteAlert />}

      <Tabs defaultValue="extra" className="w-full">
        <TabsList>
          <TabsTrigger value="extra">Données extra financières</TabsTrigger>
          <TabsTrigger value="structured">Données structurées</TabsTrigger>
          {/* <TabsTrigger value="emails">Échanges Emails</TabsTrigger> */}
          <TabsTrigger value="risque">Risque financier</TabsTrigger>
          {/* <TabsTrigger value="solutions">Solutions de financement</TabsTrigger> */}
        </TabsList>

        <TabsContent value="extra" className="space-y-6">
          <CompanyAnalysisExtra />
        </TabsContent>

        <TabsContent value="structured" className="space-y-6">
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="transactions">
                Transactions bancaires
              </TabsTrigger>
              {/* <TabsTrigger value="fiscal">Liasse fiscale</TabsTrigger> */}
            </TabsList>

            <TabsContent value="transactions">
              <BankTransactions companyId={enrichedCompany.id} />
            </TabsContent>

            {/* <TabsContent value="fiscal">
              <FiscalDataManager />
            </TabsContent> */}
          </Tabs>
        </TabsContent>

        {/* <TabsContent value="emails">
          <EmailExchanges />
        </TabsContent> */}

        <TabsContent value="risque">
          <RiskInfo company={enrichedCompany} />
        </TabsContent>

        {/* <TabsContent value="solutions">
          <FinancingSolutions />
        </TabsContent> */}
      </Tabs>
    </div>
  );
}

function CompanyAnalysisHeader() {
  const { enrichedCompany } = useCompanyAnalysis();

  return (
    <header className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {enrichedCompany.name}
        </h1>
        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Building className="h-4 w-4" />
            SIREN: {enrichedCompany.identifier}
          </div>
          <div className="flex items-center gap-1">
            <Building className="h-4 w-4" />
            SIRET: {enrichedCompany.siret_number}
          </div>
          <div className="flex items-center gap-1">
            <Badge variant={enrichedCompany.active ? "default" : "secondary"}>
              {enrichedCompany.active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>
      </div>
      <Button asChild variant="default">
        <Link to="/societes" className="inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Revenir aux sociétés
        </Link>
      </Button>
    </header>
  );
}

function CompanyAnalysisIncompleteAlert() {
  const { enrichedCompany } = useCompanyAnalysis();
  return (
    <Alert
      variant="destructive"
      className="border-red-200 bg-red-50 text-red-900"
    >
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        Les analyses de risque financier et solutions de financement peuvent
        être incomplètes en raison de données manquantes.
        <Link
          to={`/company/documents?companyId=${enrichedCompany.id}`}
          className="ml-2 underline hover:no-underline"
        >
          Compléter le dossier
        </Link>
      </AlertDescription>
    </Alert>
  );
}

function CompanyAnalysisLoading() {
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de l'entreprise...</p>
        </div>
      </div>
    </div>
  );
}

function CompanyAnalysisError() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-3xl font-bold tracking-tight">Erreur</h1>
      <p className="text-muted-foreground">
        Impossible de charger les données de l'entreprise.
      </p>
      <Button asChild>
        <Link to="/societes">Revenir aux entreprises</Link>
      </Button>
    </div>
  );
}

function CompanyAnalysisNone() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-3xl font-bold tracking-tight">Étude d'entreprise</h1>
      <p className="text-muted-foreground">Aucune société sélectionnée.</p>
      <Button asChild>
        <Link to="/societes">Voir les entreprises</Link>
      </Button>
    </div>
  );
}

function CompanyAnalysisExtra() {
  const { enrichedCompany, formatDate } = useCompanyAnalysis();

  return (
    <Fragment>
      {/* Company Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Adresse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {enrichedCompany.address.line_1}
              <br />
              {enrichedCompany.address.code} {enrichedCompany.address.town}
              <br />
              {enrichedCompany.country}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date d'immatriculation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {formatDate(enrichedCompany.immatriculation_date)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building className="h-4 w-4" />
              Code APE
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {enrichedCompany.activity_code}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Représentants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {enrichedCompany.representatives?.length || 0} représentant(s)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Representatives Section */}
      {enrichedCompany.representatives &&
        enrichedCompany.representatives.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Procédures Collectives
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {enrichedCompany.representatives.map((rep) => (
                  <div
                    key={rep.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <h4 className="font-medium">
                        {rep.first_name} {rep.last_name}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {rep.address}, {rep.zip_code} {rep.town}, {rep.country}
                      </p>
                    </div>
                    <Badge className="bg-green-500 text-white hover:bg-green-600">
                      Aucune procédure
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
    </Fragment>
  );
}
